/**
 * CartPage — review chosen services, then hand off to the landing cart to pay.
 *
 * Route: `/cart` (ProtectedRoute).
 *
 * Session 19 flow: the app stopped charging in-app. Here the user reviews the
 * services they added (proforma preview, base + 10% VAT), then "Continue to
 * payment" hands them off to the public landing cart page with their phone +
 * current clinic pre-filled and OTP already satisfied (they're authenticated
 * here, so the landing skips its OTP step). They can also keep shopping.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import {
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Box,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import Container from '@/components/PageSheet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LockIcon from '@mui/icons-material/Lock';
import StorefrontIcon from '@mui/icons-material/Storefront';

import LanguageToggle from '@/components/LanguageToggle';
import { selectCartItems, removeItem, clearCart } from '@/store/cartSlice';
import { selectLanguage, selectIsRtl } from '@/store/uiSlice';
import { selectCurrentClinic } from '@/store/clinicSlice';
import { selectUser } from '@/store/authSlice';
import { formatToman, computeProforma } from '@/utils/currency';
import { localized } from '@/utils/localizedContent';
import { buildLandingCartUrl } from '@/utils/cartHandoff';

const LANDING_URL = import.meta.env.VITE_LANDING_URL || 'https://candleai.org';

export function CartPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const language = useSelector(selectLanguage);
  const isRtl = useSelector(selectIsRtl);
  const items = useSelector(selectCartItems);
  const currentClinic = useSelector(selectCurrentClinic);
  const user = useSelector(selectUser);

  // While we hand off to the landing payment page we show a "processing"
  // screen — not the empty-cart state (which used to flash once the cart was
  // cleared just before the external redirect).
  const [redirecting, setRedirecting] = useState(false);

  const base = items.reduce((sum, it) => sum + (Number(it.priceToman) || 0), 0);
  const proforma = computeProforma(base);

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/services');
  };

  const handlePay = () => {
    const url = buildLandingCartUrl({
      landingBase: LANDING_URL,
      lang: language,
      items: items.map((it) => it.id),
      phone: user?.phone,
      clinicCode: currentClinic?.code,
    });
    // Show the processing screen first, then clear the in-app cart and leave.
    // Gating the render on `redirecting` avoids the old "cart is empty" flash.
    setRedirecting(true);
    setTimeout(() => {
      dispatch(clearCart());
      window.location.assign(url);
    }, 60);
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton size="small" onClick={handleBack} aria-label={t('common.back')}>
              {isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
            </IconButton>
            <ShoppingCartIcon color="primary" />
            <Typography variant="h6" component="h1" fontWeight={700}>
              {t('cart.title')}
            </Typography>
          </Stack>
          <LanguageToggle />
        </Stack>

        {redirecting ? (
          <Stack alignItems="center" spacing={2.5} sx={{ py: 8 }}>
            <CircularProgress />
            <Typography variant="h6" textAlign="center">
              {t('cart.processing.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {t('cart.processing.body')}
            </Typography>
          </Stack>
        ) : items.length === 0 ? (
          <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
            <ShoppingCartIcon sx={{ fontSize: 48, opacity: 0.4 }} />
            <Typography variant="h6" textAlign="center">
              {t('cart.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {t('cart.empty.body')}
            </Typography>
            <Button variant="contained" startIcon={<StorefrontIcon />} onClick={() => navigate('/services')}>
              {t('cart.empty.cta')}
            </Button>
          </Stack>
        ) : (
          <>
            {/* Items */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="overline" color="text.secondary">
                  {t('cart.itemsHeading')}
                </Typography>
                <Stack divider={<Divider />} spacing={0}>
                  {items.map((it) => (
                    <Stack
                      key={it.id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={1}
                      sx={{ py: 1.25 }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap>
                          {localized(it.name, language)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatToman(it.priceToman, language)}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        aria-label={t('cart.remove')}
                        onClick={() => dispatch(removeItem(it.id))}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Stack spacing={1}>
                  <Row label={t('cart.base')} value={formatToman(proforma.baseAmount, language)} />
                  <Row label={t('cart.tax')} value={formatToman(proforma.tax, language)} />
                  <Divider />
                  <Row label={t('cart.total')} value={formatToman(proforma.totalAmount, language)} bold />
                </Stack>
              </CardContent>
            </Card>

            {currentClinic?.code && (
              <Alert severity="info" icon={<StorefrontIcon />}>
                {t('cart.clinicPrefill', {
                  clinic: localized(currentClinic.name, language) || currentClinic.code,
                })}
              </Alert>
            )}

            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<LockIcon />}
              onClick={handlePay}
            >
              {t('cart.proceedToPayment')}
            </Button>
            <Button fullWidth variant="text" onClick={() => navigate('/services')}>
              {t('cart.continueShopping')}
            </Button>

            <Typography variant="caption" color="text.secondary" textAlign="center">
              {t('cart.handoffNote')}
            </Typography>
          </>
        )}
      </Stack>
    </Container>
  );
}

function Row({ label, value, bold }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
      <Typography variant={bold ? 'subtitle2' : 'body2'} color={bold ? 'text.primary' : 'text.secondary'}>
        {label}
      </Typography>
      <Typography
        variant={bold ? 'subtitle1' : 'body2'}
        fontWeight={bold ? 700 : 400}
        color={bold ? 'primary.main' : 'text.primary'}
      >
        {value}
      </Typography>
    </Stack>
  );
}

export default CartPage;
